// What the Expense Bills page reads from the Application API (V-09, ADR-0035): a page of bills with where each stands,
// on whom it waits and what it belongs to, and one bill opened. A bill is added as a file is attached in chat. Amounts
// are paise, absent where the person's Roles hide costs. A file added is read as a supplier's bill, never asked about;
// one LAMP couldn't read is read again, entered by hand or said to be no bill, from the page (#1006).
import { call, type Fetch } from "../http.js";

export const STANDINGS = ["to_confirm", "to_approve", "to_confirm_tax", "unpaid", "paid", "held"] as const;
export type Standing = (typeof STANDINGS)[number];

export interface NamedValue {
  readonly dimension: string;
  readonly label: string;
  readonly value: string;
  readonly name: string;
}

export interface BillRow {
  readonly id: string;
  readonly number: string;
  readonly date: string;
  readonly supplier: { readonly id: string; readonly name: string };
  readonly standing: Standing;
  readonly waitsOn: readonly string[];
  readonly yours: boolean;
  readonly total?: number;
  readonly tds?: number;
  readonly payable?: number;
  readonly values: readonly NamedValue[];
  readonly run?: string;
}

/**
 * `GET /api/expense-bills`: a page of bills, the totals of every posted bill kept and of those waiting apart (#1004), and
 * what the filters offer.
 */
export interface BillList {
  readonly bills: readonly BillRow[];
  readonly page: number;
  readonly pages: number;
  readonly count: number;
  /** How many of the bills kept are posted to the Ledgers (approved, or paid): those `totals` totals. */
  readonly posted: number;
  readonly totals?: { readonly total: number; readonly tds: number; readonly payable: number };
  /** The bills kept that are recorded and not yet posted, apart from the posted; no total where amounts are hidden. */
  readonly waiting: { readonly count: number; readonly total?: number };
  /** The bills kept in neither: held, or refused by LAMP. */
  readonly neither: number;
  readonly hidden?: readonly "amounts"[];
  readonly suppliers: readonly { readonly id: string; readonly name: string }[];
  readonly dimensions: readonly {
    readonly id: string;
    readonly label: string;
    readonly values: readonly { readonly id: string; readonly name: string }[];
  }[];
}

/** `GET /api/expense-bills/{bill}`: one bill, its lines, its file, its posting and its Run. */
export interface BillShown extends BillRow {
  readonly holdReason?: string;
  readonly cgst?: number;
  readonly sgst?: number;
  readonly igst?: number;
  readonly lines: readonly {
    readonly description: string;
    readonly account: string;
    readonly accountName: string;
    readonly amount?: number;
    readonly hsn?: string;
    readonly gstRate?: number;
    readonly values: readonly NamedValue[];
    readonly split?: readonly { readonly values: readonly NamedValue[]; readonly amount?: number }[];
  }[];
  readonly file?: { readonly id: string; readonly name: string; readonly href: string };
  readonly postings?: readonly { readonly account: string; readonly name: string; readonly amount: number }[];
}

/** The filters asked; a page counts from 1. */
export interface BillsAsked {
  readonly standing?: Standing | undefined;
  readonly supplier?: string | undefined;
  readonly value?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly page?: number | undefined;
}

/** A file to add as a bill, as chat takes it: its bytes base64-encoded. */
export interface BillFile {
  readonly name: string;
  readonly mediaType: string;
  readonly data: string;
}

/** What the page can do with a bill added that was not drafted: read it again, enter it by hand, or say it is no bill. */
export type BillFileAction = "retry" | "enter" | "choose";

/** What LAMP said of a bill added: its last message in the person's thread, and what can be done with the file next. */
export interface BillAdded {
  readonly said?: string;
  /** The Received File the bill was added as, while something on the page can be done with it. */
  readonly file?: { readonly id: string; readonly name: string; readonly actions: readonly BillFileAction[] };
}

/** A bill typed in by hand: who it is from, its number and date, what it is for, and its amounts in paise. */
export interface TypedBill {
  readonly supplier: string;
  readonly number: string;
  readonly date: string;
  readonly description: string;
  readonly amount: number;
  readonly gst: number;
  readonly total: number;
}

export interface BillsApi {
  bills(asked: BillsAsked): Promise<BillList>;
  bill(id: string): Promise<BillShown>;
  /**
   * Adds `file` as a supplier's bill, sent with `words` in the person's Language: read as a bill, as attaching it in
   * chat and saying so would be, without being asked what it is.
   */
  add(file: BillFile, words: string): Promise<BillAdded>;
  /** Reads the bill added as `file` again, after LAMP couldn't. */
  retry(file: string): Promise<BillAdded>;
  /** Drafts the bill added as `file` from what the person typed, after LAMP couldn't read it. */
  enter(file: string, bill: TypedBill): Promise<BillAdded>;
  /** Says the file added is no bill: it is kept, and nothing is started. */
  notABill(file: string): Promise<BillAdded>;
}

interface ThreadEntry {
  readonly author: string;
  readonly text?: string;
  readonly receivedFile?: { readonly id: string; readonly name: string; readonly actions: readonly string[] };
}

const PAGE_ACTIONS: readonly BillFileAction[] = ["retry", "enter", "choose"];

/** What LAMP last said, and the file's actions the page offers, from the thread answered about file `id` (or the last). */
function addedOf(entries: readonly ThreadEntry[], id?: string): BillAdded {
  const said = entries.findLast((e) => e.author === "lamp")?.text;
  const shown = entries.findLast((e) => e.receivedFile && (id === undefined || e.receivedFile.id === id))?.receivedFile;
  const actions = PAGE_ACTIONS.filter((a) => shown?.actions.includes(a));
  return {
    ...(said === undefined ? {} : { said }),
    ...(shown && actions.length > 0 ? { file: { id: shown.id, name: shown.name, actions } } : {}),
  };
}

const query = (asked: BillsAsked) =>
  new URLSearchParams(
    Object.entries(asked).flatMap(([key, value]) =>
      value === undefined || value === "" ? [] : [[key, String(value)] as [string, string]],
    ),
  ).toString();

export function createBillsApi(fetcher: Fetch = (input, init) => fetch(input, init)): BillsApi {
  return {
    bills: (asked) => call<BillList>(fetcher, `/api/expense-bills?${query(asked)}`),
    bill: async (id) => (await call<{ bill: BillShown }>(fetcher, `/api/expense-bills/${encodeURIComponent(id)}`)).bill,
    async add(file, words) {
      const { entries } = await call<{ entries: readonly ThreadEntry[] }>(fetcher, "/api/chat/files", {
        json: { ...file, text: words, kind: "supplier_bill" },
      });
      return addedOf(entries);
    },
    async retry(file) {
      const path = `/api/chat/files/${encodeURIComponent(file)}/retry`;
      return addedOf((await call<{ entries: readonly ThreadEntry[] }>(fetcher, path, { json: {} })).entries, file);
    },
    async enter(file, bill) {
      const path = `/api/chat/files/${encodeURIComponent(file)}/record`;
      return addedOf(
        (await call<{ entries: readonly ThreadEntry[] }>(fetcher, path, { json: { bill } })).entries,
        file,
      );
    },
    async notABill(file) {
      const path = `/api/chat/files/${encodeURIComponent(file)}/kind`;
      const json = { kind: "none" };
      return addedOf((await call<{ entries: readonly ThreadEntry[] }>(fetcher, path, { json })).entries, file);
    },
  };
}
